/**
 * PaymentPanel.jsx — Rê Leve IA (Etapa 9)
 * Painel de pagamento para o Site do Cliente e para o Developer Lab.
 * REGRA: nunca exibir chaves Stripe, webhooks ou dados técnicos no modo cliente.
 */

import { useState } from 'react';
import {
  createLocalPayment,
  simulateLocalPaymentSuccess,
  getKitSimulatedPrice,
  getPaymentStatusLabel,
  isPaymentApproved,
} from '../lib/paymentSimulationEngine';
import { createPaymentCheckout, simulatePaymentSuccess } from '../lib/apiClient';

// ── Modo cliente: visão simplificada ─────────────────────────────────────────

export function PaymentPanelClient({ clienteId, kit, onPaymentApproved }) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const kitName = typeof kit === 'string' ? kit : (kit?.nome || kit?.name || '');
  const price = getKitSimulatedPrice(kitName);
  const priceFormatted = `R$ ${price.toFixed(2).replace('.', ',')}`;

  async function handleCreatePayment() {
    setLoading(true);
    setError(null);
    try {
      // Tentar API real primeiro, cair para local se falhar
      const apiResult = await createPaymentCheckout({ clienteId, kitName });
      if (apiResult?.ok) {
        setPayment({ ...apiResult, source: 'api' });
      } else {
        const local = createLocalPayment({ clienteId, kit: kitName });
        setPayment({ ...local, paymentId: local.id, source: 'local' });
      }
    } catch {
      const local = createLocalPayment({ clienteId, kit: kitName });
      setPayment({ ...local, paymentId: local.id, source: 'local' });
    }
    setLoading(false);
  }

  async function handleSimulateSuccess() {
    if (!payment) return;
    setLoading(true);
    try {
      const pid = payment.paymentId || payment.id;
      if (payment.source === 'api') {
        await simulatePaymentSuccess({ paymentId: pid, clienteId });
      } else {
        simulateLocalPaymentSuccess(pid);
      }
      setPayment(prev => ({ ...prev, status: 'simulated_paid' }));
      if (onPaymentApproved) onPaymentApproved({ payment, kitName });
    } catch {
      setError('Erro ao simular aprovação. Tente novamente.');
    }
    setLoading(false);
  }

  const approved = payment && isPaymentApproved(payment.status);

  return (
    <div className="payment-panel-client">
      <div className="payment-panel-header">
        <span className="payment-panel-icon">💳</span>
        <div>
          <p className="payment-panel-title">Pagamento</p>
          <span className="payment-badge payment-badge--demo">Modo demonstração</span>
        </div>
      </div>

      <div className="payment-kit-info">
        <span className="payment-kit-name">{kitName || 'Kit não selecionado'}</span>
        <span className="payment-kit-price">{priceFormatted}</span>
      </div>

      {!payment && (
        <button
          className="cs-btn cs-btn--primary payment-btn"
          onClick={handleCreatePayment}
          disabled={loading || !kitName}
        >
          {loading ? 'Processando...' : '💳 Criar pagamento'}
        </button>
      )}

      {payment && !approved && (
        <div className="payment-pending-block">
          <p className="payment-status-label">
            ⏳ {getPaymentStatusLabel(payment.status)}
          </p>
          {payment.checkoutUrl && payment.checkoutUrl !== '#pagamento-simulado' && (
            <a
              href={payment.checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cs-btn cs-btn--primary payment-btn"
            >
              🔗 Ir para pagamento
            </a>
          )}
          <button
            className="cs-btn cs-btn--secondary payment-btn"
            onClick={handleSimulateSuccess}
            disabled={loading}
          >
            {loading ? 'Processando...' : '✅ Simular pagamento aprovado'}
          </button>
        </div>
      )}

      {approved && (
        <div className="payment-approved-block">
          <span className="payment-badge payment-badge--approved">✅ Pagamento confirmado</span>
          <p className="payment-approved-message">
            Pagamento confirmado no protótipo. O próximo passo seria finalizar separação, entrega ou atendimento pelo WhatsApp.
          </p>
        </div>
      )}

      {error && <p className="payment-error">{error}</p>}

      <p className="payment-disclaimer">
        Pagamento em modo simulado para apresentação. Nenhuma cobrança real é realizada.
      </p>
    </div>
  );
}

// ── Modo desenvolvedor: visão técnica ─────────────────────────────────────────

export function PaymentPanelDev({ clienteId, kit, payment }) {
  const kitName = typeof kit === 'string' ? kit : (kit?.nome || kit?.name || '');

  if (!payment) {
    return (
      <div className="dev-payment-panel dev-panel-empty">
        <p className="dev-panel-label">💳 Pagamentos</p>
        <p className="dev-panel-empty-text">Nenhum pagamento criado para este cliente ainda.</p>
        <p className="dev-panel-hint">Crie um pagamento no Site do Cliente para ver os detalhes aqui.</p>
      </div>
    );
  }

  const pid = payment.paymentId || payment.id || '—';

  return (
    <div className="dev-payment-panel">
      <p className="dev-panel-label">💳 Pagamentos</p>

      <div className="dev-payment-grid">
        <div className="dev-payment-row">
          <span className="dev-payment-key">Cliente</span>
          <span className="dev-payment-val">{clienteId || '—'}</span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Kit</span>
          <span className="dev-payment-val">{kitName || '—'}</span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Payment ID</span>
          <span className="dev-payment-val dev-payment-id">{pid}</span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Status</span>
          <span className="dev-payment-val">
            {payment.status}
            {isPaymentApproved(payment.status) && ' ✅'}
          </span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Modo</span>
          <span className="dev-payment-val">{payment.mode || payment.provider || '—'}</span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Valor</span>
          <span className="dev-payment-val">
            {payment.priceFormatted || (payment.amount ? `R$ ${(payment.amount / 100).toFixed(2).replace('.', ',')}` : '—')}
          </span>
        </div>
        <div className="dev-payment-row">
          <span className="dev-payment-key">Fonte</span>
          <span className="dev-payment-val">{payment.source === 'api' ? 'API backend' : 'Motor local'}</span>
        </div>
      </div>

      {payment.events && payment.events.length > 0 && (
        <div className="dev-payment-events">
          <p className="dev-payment-events-title">Eventos</p>
          {payment.events.map((ev, i) => (
            <div key={i} className="dev-payment-event">
              <span className="dev-payment-event-type">{ev.type}</span>
              <span className="dev-payment-event-at">{ev.at ? new Date(ev.at).toLocaleTimeString('pt-BR') : ''}</span>
            </div>
          ))}
        </div>
      )}

      <div className="dev-payment-n8n">
        <p className="dev-payment-n8n-title">Próxima ação n8n</p>
        {payment.status === 'simulated_pending' || payment.status === 'checkout_created' ? (
          <p className="dev-payment-n8n-action">📥 <code>checkout_created</code> → registrar intenção de compra</p>
        ) : isPaymentApproved(payment.status) ? (
          <p className="dev-payment-n8n-action">✅ <code>payment_succeeded</code> → notificar atendimento</p>
        ) : payment.status === 'failed' ? (
          <p className="dev-payment-n8n-action">⚠️ <code>payment_failed</code> → avisar humano</p>
        ) : null}
      </div>

      <div className="dev-payment-security">
        <p className="dev-payment-security-note">🔒 Nenhuma chave Stripe exibida. Dados de cartão nunca processados no front-end.</p>
      </div>
    </div>
  );
}

export default PaymentPanelClient;
